import React from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useData } from "@/context/DataContext";
import Seo from "@/components/site/Seo";
import { Reveal } from "@/components/site/Reveal";
import { WhatsAppButton } from "@/components/site/WhatsButton";
import { resolveImg } from "@/lib/api";

export default function BlogPost() {
  const { slug } = useParams();
  const { blog } = useData();
  const post = blog.find((b) => b.slug === slug);

  if (!post) {
    return (
      <div className="min-h-screen bg-av-black flex flex-col items-center justify-center px-6 text-center">
        <h1 className="font-display text-4xl text-av-white">Articolo non trovato</h1>
        <Link to="/blog" className="mt-6 text-av-lime">← Torna al blog</Link>
      </div>
    );
  }

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article", headline: post.meta_title || post.titolo, description: post.meta_description || post.estratto,
        articleSection: post.categoria, datePublished: post.data,
        image: post.immagine ? [resolveImg(post.immagine)] : undefined,
        author: { "@type": "Organization", name: "Animaverde" },
        publisher: { "@type": "Organization", name: "Animaverde" },
        mainEntityOfPage: `https://www.giardinianimaverde.it/blog/${post.slug}`,
      },
      ...(post.faq && post.faq.length > 0 ? [{
        "@type": "FAQPage",
        mainEntity: post.faq.map((f) => ({ "@type": "Question", name: f.question, acceptedAnswer: { "@type": "Answer", text: f.answer } })),
      }] : []),
    ],
  };

  return (
    <>
      <Seo title={post.meta_title || post.titolo} description={post.meta_description || post.estratto} path={`/blog/${post.slug}`} schema={schema} />
      <article className="bg-av-black">
        <div className="relative h-[52vh] min-h-[360px] overflow-hidden">
          <img src={resolveImg(post.immagine)} alt={post.titolo} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-av-black via-av-black/40 to-av-black/40" />
          <div className="absolute inset-0 flex items-end">
            <div className="mx-auto max-w-3xl w-full px-5 sm:px-8 pb-12">
              <p className="overline text-av-lime mb-3">{post.categoria}</p>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-av-white text-balance">{post.titolo}</h1>
            </div>
          </div>
        </div>

        <div className="mx-auto max-w-3xl px-5 sm:px-8 py-16">
          <Reveal>
            <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-av-gray hover:text-av-lime mb-8"><ArrowLeft size={15} /> Torna al blog</Link>
            {post.placeholder && (
              <div className="mb-8 rounded-xl border border-av-bright/40 bg-av-green-dark/40 p-4 text-sm text-av-lime" style={{ backgroundColor: "rgba(22,56,29,0.4)" }}>
                Questo articolo è una bozza dimostrativa da completare.
              </div>
            )}
            {post.content_html ? (
              <div className="blog-content" dangerouslySetInnerHTML={{ __html: post.content_html }} />
            ) : (
              <div className="space-y-5 text-av-white/85 text-lg leading-relaxed">
                {(post.contenuto || post.estratto).split("\n").filter(Boolean).map((p, i) => <p key={i}>{p}</p>)}
              </div>
            )}
            <div className="mt-12 rounded-2xl border border-white/10 p-8 text-center">
              <h2 className="font-display text-2xl text-av-white">Hai bisogno di un intervento sul tuo verde?</h2>
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                <Link to="/contatti" className="inline-flex items-center gap-2 rounded-full bg-av-bright text-av-black px-6 py-3 text-sm font-semibold hover:bg-av-lime transition-colors">Richiedi un sopralluogo</Link>
                <WhatsAppButton variant="outline" />
              </div>
            </div>
          </Reveal>
        </div>
      </article>
    </>
  );
}
